import { useEffect, useState } from 'react';
import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Switch,
  Textarea,
  tokens,
} from '@fluentui/react-components';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import { PageHeader } from '../components/PageHeader';
import { dateInputInDays } from '../features/guests/guest-format';
import type { ActionResponse } from '../api/types';

/** Sehr einfache Plausibilitätsprüfung — die verbindliche Prüfung macht die API. */
const MAIL_PATTERN = /^[^@\s]+@[^@\s.]+\.[^@\s]+$/;

export function GuestsCreatePage() {
  const api = useApi();
  const settings = useAsync(() => api.getGuestSettings(), [api]);
  const policies = useAsync(() => api.getPagePolicies(), [api]);
  const ticketMode = policies.data?.find((p) => p.pageKey === 'guests.create')?.ticketMode ?? 'None';

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [company, setCompany] = useState('');
  const [unlimited, setUnlimited] = useState(false);
  const [validUntil, setValidUntil] = useState('');
  const [message, setMessage] = useState('');
  const [sendMail, setSendMail] = useState(true);
  const [ticket, setTicket] = useState('');
  const [mailError, setMailError] = useState<string | undefined>();
  const [ticketError, setTicketError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ActionResponse | null>(null);

  // Gültigkeit mit der konfigurierten Standarddauer vorbelegen.
  useEffect(() => {
    if (settings.data && !validUntil) {
      setValidUntil(dateInputInDays(settings.data.defaultValidityDays));
    }
  }, [settings.data, validUntil]);

  const maxDate = settings.data?.maxValidityDays ? dateInputInDays(settings.data.maxValidityDays) : undefined;

  function reset() {
    setEmail('');
    setDisplayName('');
    setCompany('');
    setMessage('');
    setTicket('');
    setUnlimited(false);
    setValidUntil(dateInputInDays(settings.data?.defaultValidityDays ?? 90));
    setDone(null);
    setError(null);
    setMailError(undefined);
    setTicketError(undefined);
  }

  async function submit() {
    if (!MAIL_PATTERN.test(email.trim())) {
      setMailError('Bitte eine gültige E-Mail-Adresse angeben.');
      return;
    }
    if (ticketMode === 'Required' && !ticket.trim()) {
      setTicketError('Ticketnummer ist erforderlich.');
      return;
    }
    setMailError(undefined);
    setTicketError(undefined);
    setBusy(true);
    setError(null);
    try {
      const res = await api.inviteGuest({
        email: email.trim(),
        displayName: displayName.trim(),
        company: company.trim() || undefined,
        validUntil: unlimited ? undefined : validUntil || undefined,
        message: message.trim() || undefined,
        sendInvitationMail: sendMail,
        ticketNumber: ticket.trim() || undefined,
      });
      setDone(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div>
        <PageHeader title="Gast einladen" />
        <div style={{ maxWidth: '640px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL }}>
          {done.status === 'PendingApproval' ? (
            <MessageBar intent="info">
              <MessageBarBody>
                <MessageBarTitle>Antrag eingereicht</MessageBarTitle>
                Die Einladung ist freigabepflichtig. Den Status findest du unter „Meine Anfragen".
              </MessageBarBody>
            </MessageBar>
          ) : (
            <MessageBar intent="success">
              <MessageBarBody>
                <MessageBarTitle>Erledigt</MessageBarTitle>
                {done.message}
              </MessageBarBody>
            </MessageBar>
          )}
          <div>
            <Button appearance="primary" onClick={reset}>
              Weiteren Gast einladen
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const canSubmit = MAIL_PATTERN.test(email.trim()) && displayName.trim().length > 0 && !busy;

  return (
    <div>
      <PageHeader
        title="Gast einladen"
        description="B2B-Einladung für einen Gast aus einem fremden Tenant — mit zeitlich begrenzter Gültigkeit."
      />
      <div style={{ maxWidth: '560px', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL }}>
        {error ? (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        ) : null}

        <Field label="E-Mail des Gasts" required validationMessage={mailError}>
          <Input
            type="email"
            value={email}
            onChange={(_, d) => setEmail(d.value)}
            placeholder="vorname.nachname@partnerfirma.de"
          />
        </Field>

        <Field label="Anzeigename" required hint="So erscheint der Gast im Verzeichnis.">
          <Input value={displayName} onChange={(_, d) => setDisplayName(d.value)} placeholder="z. B. Erika Mustermann" />
        </Field>

        <Field label="Firma" hint="Wird am Gastkonto hinterlegt und in der Übersicht angezeigt.">
          <Input value={company} onChange={(_, d) => setCompany(d.value)} placeholder="z. B. Partnerfirma GmbH" />
        </Field>

        <Field
          label="Gültig bis"
          hint={
            unlimited
              ? 'Der Zugang läuft nicht automatisch ab.'
              : settings.data?.enforceExpiry
                ? 'Nach Ablauf wird der Zugang automatisch gesperrt (umkehrbar über „Ändern").'
                : 'Ablauf wird nur angezeigt, nicht automatisch durchgesetzt.'
          }
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
            <Input
              type="date"
              value={validUntil}
              disabled={unlimited}
              min={dateInputInDays(0)}
              max={maxDate}
              onChange={(_, d) => setValidUntil(d.value)}
            />
            <Switch checked={unlimited} onChange={(_, d) => setUnlimited(d.checked)} label="unbefristet" />
          </div>
        </Field>

        <Field label="Nachricht an den Gast" hint="Optionaler Text in der Einladungs-Mail.">
          <Textarea value={message} onChange={(_, d) => setMessage(d.value)} rows={3} />
        </Field>

        <Switch
          checked={sendMail}
          onChange={(_, d) => setSendMail(d.checked)}
          label="Einladungs-Mail durch Microsoft versenden"
        />

        {ticketMode !== 'None' ? (
          <Field
            label="Ticketnummer"
            required={ticketMode === 'Required'}
            validationMessage={ticketError}
            hint={ticketMode === 'Required' ? 'Pflicht für diese Aktion.' : 'Optional.'}
          >
            <Input value={ticket} onChange={(_, d) => setTicket(d.value)} placeholder="z. B. REQ0012345" />
          </Field>
        ) : null}

        <div>
          <Button appearance="primary" disabled={!canSubmit} onClick={submit}>
            {busy ? 'Bitte warten…' : 'Einladung senden'}
          </Button>
        </div>
      </div>
    </div>
  );
}
